import { A } from "@ember/array"
import type NativeArray from "@ember/array/-private/native-array"
import { cancel, later, run } from "@ember/runloop"
import Service from "@ember/service"
import { tracked } from "@glimmer/tracking"

// Notifier service based on ember-notifier, https://github.com/scottwernervt/ember-notifier
// which is MIT licensed.
// Modified to upgrade to Ember Octane + Glimmer + TypeScript and remove some parts we don't use.

const CONFIG = {
  position: "is-top-right",
  duration: 4200, // ms

  primaryClass: "is-primary",
  infoClass: "is-info",
  successClass: "is-success",
  warningClass: "is-warning",
  dangerClass: "is-danger",
  secondaryClass: "is-secondary",

  // primaryIcon: "fas fa-bell",
  // infoIcon: "fas fa-info",
  // successIcon: "fas fa-check",
  // warningIcon: "fas fa-exclamation",
  // dangerIcon: "fas fa-fire",
  // secondaryIcon: "fas fa-comment",
  primaryIcon: "toast-notice__icon--primary",
  infoIcon: "toast-notice__icon--info",
  successIcon: "toast-notice__icon--success",
  warningIcon: "toast-notice__icon--warning",
  dangerIcon: "toast-notice__icon--danger",
  secondaryIcon: "toast-notice__icon--secondary",

  showAnimationClass: "ember-notifier-notification-show",
  hideAnimationClass: "ember-notifier-notification-hide",
  animationTimeout: 500, // ms
  minSwipeDistance: 150, // pixels
  maxSwipeTime: 300, // ms
  swipeDirection: "right", // 'left' or 'right'
}

type Config = typeof CONFIG

export interface NotificationOptions {
  type?: string
  duration?: number
  animationTimeout?: number
  minSwipeDistance?: number
  maxSwipeTime?: number
  swipeDirection?: string
  message?: string
  title?: string
  contentComponent?: string
  icon?: string
  onRemove?: () => void
  notificationId?: string
}

export class EmberNotification {
  type: string
  duration: number
  @tracked animationState: string
  animationTimeout: number
  minSwipeDistance: number
  maxSwipeTime: number
  swipeDirection: string
  timer: ReturnType<typeof later> | null = null
  onRemove: () => void
  message?: string
  title?: string
  contentComponent?: string
  icon?: string
  notificationId?: string

  constructor(defaults: Config & { animationState: string }, options: NotificationOptions) {
    this.type = options.type ?? defaults.primaryClass
    this.duration = options.duration ?? defaults.duration
    this.animationState = defaults.animationState
    this.animationTimeout = options.animationTimeout ?? defaults.animationTimeout
    this.minSwipeDistance = options.minSwipeDistance ?? defaults.minSwipeDistance
    this.maxSwipeTime = options.maxSwipeTime ?? defaults.maxSwipeTime
    this.swipeDirection = options.swipeDirection ?? defaults.swipeDirection
    this.onRemove = options.onRemove ?? (() => void 0)
    this.message = options.message
    this.title = options.title
    this.contentComponent = options.contentComponent
    this.icon = options.icon
    this.notificationId = options.notificationId
  }
}

export default class NotifierService extends Service {
  notifications: NativeArray<EmberNotification> = A()

  primary(message: string, options: NotificationOptions = {}) {
    this.add({ message, type: CONFIG.primaryClass, icon: CONFIG.primaryIcon, ...options })
  }

  info(message: string, options: NotificationOptions = {}) {
    this.add({ message, type: CONFIG.infoClass, icon: CONFIG.infoIcon, ...options })
  }

  success(message: string, options: NotificationOptions = {}) {
    this.add({ message, type: CONFIG.successClass, icon: CONFIG.successIcon, ...options })
  }

  warning(message: string, options: NotificationOptions = {}) {
    this.add({ message, type: CONFIG.warningClass, icon: CONFIG.warningIcon, ...options })
  }

  danger(message: string, options: NotificationOptions = {}) {
    this.add({ message, type: CONFIG.dangerClass, icon: CONFIG.dangerIcon, ...options })
  }

  secondary(message: string, options: NotificationOptions = {}) {
    this.add({ message, type: CONFIG.secondaryClass, icon: CONFIG.secondaryIcon, ...options })
  }

  add(options: NotificationOptions = {}) {
    if (!options.message && !options.contentComponent) {
      throw new Error("No message or contentComponent set.")
    }

    const notification = new EmberNotification(
      { ...CONFIG, animationState: CONFIG.showAnimationClass },
      options
    )
    this.notifications.insertAt(0, notification)

    if (notification.duration > 0) {
      this.scheduleRemoval(notification)
    }
  }

  remove(notification: EmberNotification) {
    this.cancelRemoval(notification)
    run(this, () => (notification.animationState = CONFIG.hideAnimationClass))
    later(
      this,
      () => {
        notification.onRemove()
        this.notifications.removeObject(notification)
      },
      notification.animationTimeout
    )
  }

  empty() {
    this.notifications.forEach((notification) => this.remove(notification))
  }

  scheduleRemoval(notification: EmberNotification) {
    notification.timer = later(this, () => this.remove(notification), notification.duration)
  }

  cancelRemoval(notification: EmberNotification) {
    if (notification.timer !== null) {
      cancel(notification.timer)
    }
  }
}
